import { ApolloClient } from '@apollo/client';
import { ACCESSIBLE_CANDIDATE_IDS, APPLIED_CANDIDATE_IDS_FOR_JOB } from './gql';
import { NormalizedRow } from './types';

/**
 * The ATS-side "must not render" pass every pool tab runs over recsys results
 * before they reach the page. Lifted out of index.tsx (kan-119) so the
 * applicant-mode branch is testable without mounting the whole page; the
 * behavior for the original call sites is unchanged.
 *
 * Drops rows the recruiter must not see before they render:
 *  - candidates already applied/dispositioned (shortlisted/declined) for the
 *    selected job (appliedCandidateIdsForJob union — job-scoped)
 *  - PRIVATE candidates the recruiter isn't authorized to access
 *    (accessibleCandidateIds — candidate-scoped, so it runs even with no job)
 * recsys is PII-blind and returns private candidates' ids like any other, so
 * this ATS-side pass is what keeps them out of the pool. Fails OPEN on any
 * error — candidatesByIds hydration is still the hard identity gate
 * downstream, so a failure here degrades to blank rows, never a PII leak.
 *
 * skipApplied (kan-119 applicant mode): the pool IS the job's applicants, so
 * the applied exclusion would hide every single row — it is skipped (no query
 * at all). The private-candidate gate always runs: an applicant pool is still
 * subject to candidate privacy.
 */
export type HiddenRowsClient = Pick<ApolloClient<unknown>, 'query'>;

// The two ATS lookups are bounded at the same 400 ids the resolvers accept;
// anything beyond is the rare tail (recsys clamps topN at 100 anyway).
const MAX_CHECKED_IDS = 400;

export const filterHiddenRows = async (
  client: HiddenRowsClient,
  jobId: string | null,
  candidateRows: NormalizedRow[],
  options: { skipApplied?: boolean } = {}
): Promise<NormalizedRow[]> => {
  if (!candidateRows.length) return candidateRows;
  const checkedIds = candidateRows.slice(0, MAX_CHECKED_IDS).map((r) => r.candidateId);
  const checkedSet = new Set(checkedIds);
  const runApplied = !!jobId && !options.skipApplied;
  try {
    const [appliedRes, accessibleRes] = await Promise.all([
      runApplied
        ? client.query<{ appliedCandidateIdsForJob: string[] }>({
            query: APPLIED_CANDIDATE_IDS_FOR_JOB,
            variables: { jobOrderId: jobId, candidateIds: checkedIds },
            fetchPolicy: 'network-only',
          })
        : Promise.resolve({ data: { appliedCandidateIdsForJob: [] as string[] } }),
      client.query<{ accessibleCandidateIds: string[] }>({
        query: ACCESSIBLE_CANDIDATE_IDS,
        variables: { candidateIds: checkedIds },
        fetchPolicy: 'network-only',
      }),
    ]);
    const excluded = new Set(appliedRes.data.appliedCandidateIdsForJob);
    const accessible = new Set(accessibleRes.data.accessibleCandidateIds);
    return candidateRows.filter((r) => {
      if (excluded.has(r.candidateId)) return false;
      // Enforce the private-candidate gate only for the ≤400 ids we actually
      // checked; keep any rare tail beyond the cap (identity is still gated
      // downstream by candidatesByIds).
      if (checkedSet.has(r.candidateId) && !accessible.has(r.candidateId)) return false;
      return true;
    });
  } catch (err) {
    console.warn('[candidate-recommendations] applied/accessible lookup failed; showing unfiltered results', err);
    return candidateRows;
  }
};
